import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import SearchField from 'src/shared/components/form/searchField';
import { PageFilterSection } from 'src/shared/components/organisms/pageFilterSection';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { useLocalStorage } from 'src/shared/hooks/useLocalStorage';
import { LocalStorageKeys } from 'src/shared/models/enums/localStorageKeys.enum';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { Alert, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE } from 'src/shared/models/enums/gridFilterConfig';
import { DonorAutogiroFilter } from '../models/donorAutogiroFilter';
import { MandateActionFilter } from '../components/mandateActionFilter';
import { useDonorActionRequiredBadge } from '../hooks/useDonorActionRequiredBadge';
import { DonorAutogiroDataGrid } from '../components/donorAutogiroDataGrid';

// Sorteringen måste peka på ett fält som finns som kolumn i griden. Gör den inte det nollställer
// MUI sorteringsmodellen, vilket triggar onSortModelChange, som skriver om filtret och hämtar om
// listan i en loop. Därför både ett giltigt standardvärde och en kontroll av det som ligger
// sparat i local storage.
const SORTABLE_COLUMNS = ['name', 'personalIdentityNumber', 'createdAt'];
const DEFAULT_SORT_COL = 'createdAt';

export const DonorAutogiroOverviewPage = () => {
  const isMounted = useIsMounted();
  const location = useLocation();
  const { t } = useTranslation();
  const [donorActionRequiredCount] = useDonorActionRequiredBadge();
  const [tempSearchString, setTempSearchString] = useState<string | undefined>(undefined);
  const [searchInitialized, setSearchInitialized] = useState<boolean>(false);
  const [donorAutogiroFilter, setDonorAutogiroFilter] = useLocalStorage(LocalStorageKeys.DonorAutogiroFilter, {
    sortCol: DEFAULT_SORT_COL,
    sortDir: 'desc',
    pageIndex: DEFAULT_PAGE_INDEX,
    pageSize: DEFAULT_PAGE_SIZE,
  } as DonorAutogiroFilter);

  const searchFilter: DonorAutogiroFilter = useMemo(() => {
    return {
      filter: donorAutogiroFilter?.filter,
      pageSize: donorAutogiroFilter?.pageSize,
      pageIndex: donorAutogiroFilter?.pageIndex,
      sortCol: SORTABLE_COLUMNS.includes(donorAutogiroFilter?.sortCol) ? donorAutogiroFilter?.sortCol : DEFAULT_SORT_COL,
      sortDir: donorAutogiroFilter?.sortDir,
      onlyBlocked: donorAutogiroFilter?.onlyBlocked,
    } as DonorAutogiroFilter;
  }, [
    donorAutogiroFilter?.filter,
    donorAutogiroFilter?.onlyBlocked,
    donorAutogiroFilter?.pageIndex,
    donorAutogiroFilter?.pageSize,
    donorAutogiroFilter?.sortCol,
    donorAutogiroFilter?.sortDir,
  ]);

  // Kommer man från Att hantera på faktureringsöversikten ska filtret redan vara på.
  useEffect(() => {
    const state = location.state as { onlyBlocked?: boolean } | null;

    if (state?.onlyBlocked) {
      setDonorAutogiroFilter((prev) => ({ ...prev, onlyBlocked: true, pageIndex: 0 }));
      window.history.replaceState({}, '');
    }
    // Bara vid ankomst
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isMounted() && searchInitialized) {
      if (tempSearchString && tempSearchString.length >= 2) {
        setDonorAutogiroFilter((prev) => ({ ...prev, ...{ filter: tempSearchString, pageIndex: 0 } }));
      } else {
        setDonorAutogiroFilter((prev) => ({ ...prev, ...{ filter: '', pageIndex: 0 } }));
      }
    }
  }, [isMounted, searchInitialized, setDonorAutogiroFilter, tempSearchString]);

  return (
    <>
      <Alert severity='info' sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {t('invoice:autogiro.donorAlertInfoText')}
      </Alert>
      <PageFilterSection
        leftContent={
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <SearchField
              initialValue={searchFilter.filter || ''}
              onSearch={(newSearchString) => {
                setSearchInitialized(true);
                setTempSearchString(newSearchString);
              }}
            />
            <MandateActionFilter
              value={!!searchFilter.onlyBlocked}
              count={donorActionRequiredCount}
              callback={(onlyBlocked) =>
                setDonorAutogiroFilter((prev) => ({
                  ...prev,
                  onlyBlocked: onlyBlocked || undefined,
                  pageIndex: 0,
                }))
              }
            />
          </Box>
        }
      />
      <DonorAutogiroDataGrid
        loadingCallback={(x) => ConsoleHelper.log(x)}
        searchFilter={searchFilter}
        searchFilterCallback={(newFilter) =>
          setDonorAutogiroFilter((prev) => ({
            ...prev,
            ...newFilter,
          }))
        }
      />
    </>
  );
};
